
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSalesReport } from '../../hooks/useSalesReport';
import { BudgetService } from '../../services/budget.service';
import { format, startOfMonth, endOfMonth, addDays, differenceInCalendarDays } from 'date-fns';
import {
    BarChart3, ArrowLeft, Calendar as CalendarIcon, DollarSign,
    TrendingUp, Loader2, RefreshCw, Trash2, Settings, Download, X
} from 'lucide-react';
import {
    Dialog, DialogContent, DialogTrigger, DialogHeader, DialogTitle, DialogClose
} from "@/components/ui/dialog";
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import DatePicker, { registerLocale } from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { es } from 'date-fns/locale';
registerLocale('es', es);
import { cn } from '@/lib/utils';
import { formatCurrency } from '../../lib/utils';
import DashboardHeader from './DashboardHeader';
import DatePresetButtons, { getPresetRange, DEFAULT_PRESET } from '../common/DatePresetButtons';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// --- Helpers ---
const toDateStr = (d) => format(d, 'yyyy-MM-dd');

const defaultDailyWeights = {
    0: { p: 1.20, s: 2.18 },
    1: { p: 0.64, s: 1.16 },
    2: { p: 0.72, s: 1.31 },
    3: { p: 0.96, s: 1.74 },
    4: { p: 1.21, s: 2.19 },
    5: { p: 1.84, s: 2.65 },
    6: { p: 1.84, s: 3.34 },
};

const defaultBranchBudget = {
    total: 0,
    sellerGoals: [],
    dailyWeights: defaultDailyWeights,
};

export default function AnalyticsScreen() {
    const { user } = useAuth();

    const [datePreset, setDatePreset] = useState(DEFAULT_PRESET);
    const [dateRange, setDateRange] = useState(() => {
        const r = getPresetRange(DEFAULT_PRESET);
        return { startDate: toDateStr(r.from), endDate: toDateStr(r.to) };
    });

    const [selectedBranch, setSelectedBranch] = useState('ALL');
    const [branches, setBranches]   = useState(['ALL']);
    const [budgets, setBudgets]     = useState({});
    const [budgetsLoaded, setBudgetsLoaded] = useState(false);
    const [isBudgetOpen, setIsBudgetOpen]   = useState(false);
    const [availableSellers, setAvailableSellers] = useState([]);

    const { report, loading, getSalesReport, clearReport } = useSalesReport();

    // Load saved budgets once
    useEffect(() => {
        BudgetService.getBudgets().then(saved => {
            if (saved) setBudgets(saved);
            setBudgetsLoaded(true);
        });
    }, []);

    // Initial fetch
    useEffect(() => {
        if (budgetsLoaded) handleGenerate();
    }, [budgetsLoaded]); // eslint-disable-line

    // Derive branches + sellers from report data
    useEffect(() => {
        if (!report?.orders) return;

        const sellerSet  = new Set();
        const branchSet  = new Set();

        report.orders.forEach(o => {
            const name   = o.sellerName || o.seller;
            const branch = o.branch || 'Sucursal Principal';
            if (name && name !== 'Sin Asignar') sellerSet.add(name.trim());
            if (branch && !branch.includes('ALMACEN')) branchSet.add(branch);
        });

        // Also add sellers from report.sellers (covers sellers with orders in other periods)
        report.sellers?.forEach(s => {
            if (s.sellerName && s.sellerName !== 'Sin Asignar') sellerSet.add(s.sellerName.trim());
        });

        const branchList  = ['ALL', ...Array.from(branchSet).sort()];
        const sellerList  = Array.from(sellerSet).sort().map(name => ({
            name,
            role: 'secundario',
            goalPercentage: 0,
        }));

        setBranches(branchList);
        setAvailableSellers(sellerList);

        // Initialize budget entries for new branches (don't overwrite existing)
        setBudgets(prev => {
            const next = { ...prev };
            branchList.filter(b => b !== 'ALL').forEach(b => {
                if (!next[b]) {
                    next[b] = { ...defaultBranchBudget, sellerGoals: sellerList.map(s => ({ ...s })) };
                } else {
                    // Add missing sellers to existing branch budgets
                    const existing = new Set(next[b].sellerGoals.map(s => s.name));
                    sellerList.forEach(s => {
                        if (!existing.has(s.name)) next[b].sellerGoals.push({ ...s });
                    });
                }
            });
            return next;
        });
    }, [report]);

    const handleGenerate = (range = dateRange) => {
        getSalesReport(range.startDate, range.endDate);
    };

    const handleClearCache = () => {
        clearReport();
        handleGenerate();
    };

    const handlePreset = (preset, range) => {
        const next = { startDate: toDateStr(range.from), endDate: toDateStr(range.to) };
        setDatePreset(preset);
        setDateRange(next);
        handleGenerate(next);
    };

    // --- Budget totals ---
    const activeBudgetTotal = useMemo(() => {
        if (selectedBranch !== 'ALL') return budgets[selectedBranch]?.total || 0;
        return Object.values(budgets).reduce((a, b) => a + (b.total || 0), 0);
    }, [budgets, selectedBranch]);

    // --- Core: daily breakdown ---
    const dailyReportData = useMemo(() => {
        if (!report?.orders?.length) return { days: [], sellersWithGoals: [], totalEstimate: 0, totalActual: 0 };

        const start = new Date(dateRange.startDate + 'T00:00:00');
        const end   = new Date(dateRange.endDate   + 'T23:59:59');

        // Filter by branch
        const filteredOrders = report.orders.filter(o =>
            selectedBranch === 'ALL' ||
            (o.branch || 'Sucursal Principal') === selectedBranch
        );

        // Aggregate sales by day (key = "YYYY-MM-DD")
        const salesByDay = new Map();
        filteredOrders.forEach(order => {
            const rawDate = order.date || order.orderDate || order.creationDate || '';
            if (!rawDate) return;

            // Extract "YYYY-MM-DD" regardless of whether it's a full ISO string or bare date
            const dayKey = rawDate.substring(0, 10);
            if (dayKey < dateRange.startDate || dayKey > dateRange.endDate) return;

            const bucket = salesByDay.get(dayKey) || { total: 0, bySeller: {} };
            // Use subtotal (netSales = pre-tax) for analytics
            const amount = Number(order.netSales ?? order.subtotal ?? 0);
            const seller = order.sellerName || order.seller || 'Sin Asignar';

            bucket.total += amount;
            bucket.bySeller[seller] = (bucket.bySeller[seller] || 0) + amount;
            salesByDay.set(dayKey, bucket);
        });

        // Determine which branches contribute budgets
        const involvedBranches = selectedBranch === 'ALL'
            ? branches.filter(b => b !== 'ALL' && !b.includes('ALMACEN'))
            : [selectedBranch];

        // Build active sellers set (has goals or has actual sales)
        const activeSellersSet = new Set();
        involvedBranches.forEach(b => {
            (budgets[b]?.sellerGoals || []).forEach(sg => {
                if (sg.goalPercentage > 0) activeSellersSet.add(sg.name);
            });
        });
        filteredOrders.forEach(o => {
            const name = o.sellerName || o.seller;
            if (name && name !== 'Sin Asignar' && (o.netSales > 0 || o.subtotal > 0))
                activeSellersSet.add(name);
        });

        const activeSellersList = Array.from(activeSellersSet).sort();

        // Pre-compute monthly weight totals per branch (avoid inner loop inside day loop)
        const branchWeightCache = {};
        involvedBranches.forEach(b => {
            const bBudget = budgets[b];
            if (!bBudget) return;
            const monthStart = new Date(start.getFullYear(), start.getMonth(), 1);
            const monthEnd   = endOfMonth(monthStart);
            let wp = 0, ws = 0;
            for (let d = new Date(monthStart); d <= monthEnd; d = addDays(d, 1)) {
                const w = bBudget.dailyWeights?.[d.getDay()] || defaultDailyWeights[d.getDay()];
                wp += w.p || 0;
                ws += w.s || 0;
            }
            branchWeightCache[b] = { wp, ws };
        });

        const days = [];
        let totalEstimate = 0, totalActual = 0;

        for (let cur = new Date(start); cur <= end; cur = addDays(cur, 1)) {
            const dayKey = toDateStr(cur);
            const dow    = cur.getDay();
            const bucket = salesByDay.get(dayKey) || { total: 0, bySeller: {} };

            let dailyEstimate = 0;
            const sellerData  = {};

            involvedBranches.forEach(b => {
                const bBudget = budgets[b];
                if (!bBudget?.total) return;
                const cache = branchWeightCache[b];
                if (!cache) return;
                const w = bBudget.dailyWeights?.[dow] || defaultDailyWeights[dow];

                (bBudget.sellerGoals || []).forEach(sg => {
                    const pct = sg.goalPercentage || 0;
                    if (pct === 0) return;
                    const role  = sg.role === 'principal' ? 'p' : 's';
                    const wTot  = role === 'p' ? cache.wp : cache.ws;
                    const wDay  = role === 'p' ? w.p : w.s;
                    if (!wTot) return;
                    const goal  = bBudget.total * (wDay / wTot) * (pct / 100);
                    dailyEstimate += goal;
                    if (!sellerData[sg.name]) sellerData[sg.name] = { goal: 0, actual: 0 };
                    sellerData[sg.name].goal += goal;
                });
            });

            activeSellersList.forEach(name => {
                if (!sellerData[name]) sellerData[name] = { goal: 0, actual: 0 };
                sellerData[name].actual += bucket.bySeller[name] || 0;
            });

            const fulfillment = dailyEstimate > 0 ? (bucket.total / dailyEstimate) * 100 : 0;

            days.push({
                date:           format(cur, 'dd/MM/yyyy'),
                dayName:        format(cur, 'EEEE', { locale: es }),
                estimatedSales: dailyEstimate,
                totalDaySales:  bucket.total,
                fulfillment,
                sellerData,
            });

            totalEstimate += dailyEstimate;
            totalActual   += bucket.total;
        }

        return {
            days,
            sellersWithGoals: activeSellersList.map(n => ({ name: n })),
            totalEstimate,
            totalActual,
        };
    }, [report, budgets, dateRange, selectedBranch, branches]);

    // --- KPI stats ---
    const stats = useMemo(() => {
        if (!report?.orders?.length) return { totalTickets: 0, totalRevenue: 0, averageTicket: 0, totalUnits: 0, totalTax: 0 };

        const filtered = selectedBranch === 'ALL'
            ? report.orders
            : report.orders.filter(o => (o.branch || 'Sucursal Principal') === selectedBranch);

        let tickets = 0, revenue = 0, units = 0, tax = 0;
        filtered.forEach(o => {
            tickets++;
            revenue += Number(o.netSales ?? o.subtotal ?? 0);
            tax     += Number(o.tax ?? 0);
            (o.items || []).forEach(item => { units += Number(item.quantity || 0); });
        });

        return {
            totalTickets:  tickets,
            totalRevenue:  revenue,
            averageTicket: tickets ? revenue / tickets : 0,
            totalUnits:    units,
            totalTax:      tax,
        };
    }, [report, selectedBranch]);

    const periodBudget      = dailyReportData.totalEstimate;
    const periodActual      = dailyReportData.totalActual;
    const periodFulfillment = periodBudget > 0 ? (periodActual / periodBudget) * 100 : 0;
    const monthFulfillment  = activeBudgetTotal > 0 ? (periodActual / activeBudgetTotal) * 100 : 0;
    const UPT               = stats.totalTickets > 0 ? stats.totalUnits / stats.totalTickets : 0;

    // --- Budget save ---
    const handleBudgetSave = async (updated) => {
        setBudgets(updated);
        await BudgetService.saveBudgets(updated);
        setIsBudgetOpen(false);
    };

    // --- PDF Export ---
    const handleExportPDF = () => {
        if (!dailyReportData.days.length) return;
        const doc = new jsPDF({ orientation: 'landscape' });
        doc.setFontSize(14);
        doc.text(`Reporte de Metas — ${selectedBranch === 'ALL' ? 'Todas las Sucursales' : selectedBranch}`, 14, 14);
        doc.setFontSize(9);
        doc.text(`Período: ${dateRange.startDate} al ${dateRange.endDate}`, 14, 20);
        doc.text(`Presupuesto: ${formatCurrency(activeBudgetTotal)}`, 14, 26);

        const headers = ['Fecha', 'Día', 'Meta', 'Real', '%Cumpl',
            ...dailyReportData.sellersWithGoals.map(s => s.name.split(' ')[0])];
        const body = dailyReportData.days.map(d => [
            d.date,
            d.dayName.charAt(0).toUpperCase() + d.dayName.slice(0, 3),
            formatCurrency(d.estimatedSales),
            formatCurrency(d.totalDaySales),
            d.fulfillment.toFixed(0) + '%',
            ...dailyReportData.sellersWithGoals.map(s => {
                const sd = d.sellerData[s.name];
                if (!sd) return '-';
                const pct = sd.goal > 0 ? ((sd.actual / sd.goal) * 100).toFixed(0) + '%' : '';
                return `${formatCurrency(sd.actual)}${pct ? ` (${pct})` : ''}`;
            }),
        ]);

        // Totals row
        body.push([
            'TOTAL', '',
            formatCurrency(periodBudget),
            formatCurrency(periodActual),
            periodFulfillment.toFixed(0) + '%',
            ...dailyReportData.sellersWithGoals.map(s => {
                const total = dailyReportData.days.reduce((acc, d) => acc + (d.sellerData[s.name]?.actual || 0), 0);
                return formatCurrency(total);
            }),
        ]);

        autoTable(doc, {
            head: [headers],
            body,
            startY: 30,
            styles: { fontSize: 7, cellPadding: 1.5 },
            headStyles: { fillColor: [22, 163, 74] },
            didDrawRow: (data) => {
                if (data.row.index === body.length - 1) {
                    data.doc.setFillColor(30, 41, 59);
                }
            }
        });
        doc.save(`metas_${selectedBranch}_${dateRange.startDate}.pdf`);
    };

    const KPI_CARDS = [
        { title: 'Presupuesto Mensual',         value: formatCurrency(activeBudgetTotal) },
        { title: 'Venta Real (Período)',         value: formatCurrency(periodActual),         color: 'text-green-400' },
        { title: 'Meta del Período',             value: formatCurrency(periodBudget) },
        { title: 'Cumplimiento Período',         value: `${periodFulfillment.toFixed(1)}%`,   color: periodFulfillment >= 100 ? 'text-green-400' : 'text-red-400' },
        { title: 'Cumplimiento del Mes',         value: `${monthFulfillment.toFixed(1)}%` },
        { title: 'No. Pedidos',                  value: stats.totalTickets },
        { title: 'Ticket Promedio',              value: formatCurrency(stats.averageTicket) },
        { title: 'UPT (Unidades/Pedido)',        value: UPT.toFixed(2) },
        { title: 'Impuestos Período',            value: formatCurrency(stats.totalTax),       color: 'text-blue-400' },
        { title: 'Unidades Vendidas',            value: stats.totalUnits },
    ];

    return (
        <div className="min-h-screen bg-gray-950 text-white px-4 py-5 md:p-6 space-y-4 md:space-y-6">
            <DashboardHeader activeTab="analytics" />

            {/* Controls */}
            <Card className="bg-white/5 border-white/10 text-white">
                <CardContent className="p-3 sm:p-4 flex flex-col lg:flex-row gap-3 lg:justify-between lg:items-center">
                    <div className="flex flex-col gap-2 min-w-0">
                        <h2 className="hidden md:block text-lg font-bold">Analytics & Metas</h2>
                        <DatePresetButtons value={datePreset} onChange={handlePreset} disabled={loading} />
                    </div>

                    <div className="grid grid-cols-2 sm:flex gap-2 sm:gap-3 sm:items-center sm:flex-wrap [&>*:first-child]:col-span-2 [&>select]:col-span-2">
                        {/* Date picker */}
                        <Dialog>
                            <DialogTrigger asChild>
                                <Button variant="outline" className="h-10 px-4 border-white/10 text-white bg-black/40 hover:bg-white/5">
                                    <CalendarIcon className="mr-2 h-4 w-4" />
                                    {dateRange.startDate} → {dateRange.endDate}
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="w-auto p-6 bg-slate-950 border-white/10 text-white sm:max-w-xl">
                                <DialogHeader>
                                    <DialogTitle className="text-white mb-4 text-center">Seleccionar Período</DialogTitle>
                                </DialogHeader>
                                <div className="flex flex-col items-center rounded-lg border border-white/5 bg-black/30 p-4">
                                    <style>{`
                                        .react-datepicker { background:#0f172a; border-color:#334155; color:#f8fafc; font-family:inherit; }
                                        .react-datepicker__header { background:#1e293b; border-color:#334155; }
                                        .react-datepicker__current-month,.react-datepicker__day-name { color:#94a3b8; }
                                        .react-datepicker__day { color:#f8fafc; }
                                        .react-datepicker__day:hover { background:#334155; }
                                        .react-datepicker__day--selected,.react-datepicker__day--in-range,.react-datepicker__day--in-selecting-range { background:#16a34a; color:#fff; }
                                        .react-datepicker__day--keyboard-selected { background:#22c55e; }
                                        .react-datepicker__day--disabled { color:#475569; }
                                    `}</style>
                                    <DatePicker
                                        selected={new Date(dateRange.startDate + 'T00:00:00')}
                                        onChange={([start, end]) => {
                                            setDatePreset('custom');
                                            setDateRange({
                                                startDate: start ? toDateStr(start) : dateRange.startDate,
                                                endDate:   end   ? toDateStr(end)   : null,
                                            });
                                        }}
                                        startDate={new Date(dateRange.startDate + 'T00:00:00')}
                                        endDate={dateRange.endDate ? new Date(dateRange.endDate + 'T00:00:00') : null}
                                        selectsRange inline locale="es" monthsShown={2}
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                    <DialogClose asChild>
                                        <Button variant="outline" className="border-white/10 text-white hover:bg-white/10">Cancelar</Button>
                                    </DialogClose>
                                    <DialogClose asChild>
                                        <Button className="bg-green-600 hover:bg-green-500 text-white font-bold">Aplicar</Button>
                                    </DialogClose>
                                </div>
                            </DialogContent>
                        </Dialog>

                        {/* Branch selector */}
                        <select
                            className="bg-black/40 border border-white/10 rounded px-3 py-2 text-white h-10"
                            value={selectedBranch}
                            onChange={e => setSelectedBranch(e.target.value)}
                        >
                            {branches.map(b => (
                                <option key={b} value={b}>{b === 'ALL' ? '🌐 Todas las Sucursales' : b}</option>
                            ))}
                        </select>

                        <Button variant="outline" className="h-10 border-white/10 text-white" onClick={() => setIsBudgetOpen(true)}>
                            <Settings className="mr-2 h-4 w-4" /> Presupuesto
                        </Button>
                        <Button onClick={() => handleGenerate()} className="h-10 px-4 sm:px-6 bg-green-600 hover:bg-green-500 font-bold" disabled={loading}>
                            <RefreshCw className={`mr-2 h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
                            {loading ? 'Cargando…' : 'Generar'}
                        </Button>
                        <Button variant="ghost" onClick={handleClearCache} className="h-10 col-span-2 sm:w-10 sm:px-0 text-red-400 hover:bg-red-950/30" title="Borrar caché y recargar">
                            <Trash2 className="h-4 w-4" /><span className="sm:hidden">Limpiar caché</span>
                        </Button>
                    </div>
                </CardContent>
            </Card>

            {/* Loading state */}
            {loading && (
                <div className="flex items-center justify-center gap-3 py-6 text-white/60">
                    <Loader2 className="h-5 w-5 animate-spin text-green-400" />
                    <span>Cargando datos desde DynamiaERP… esto puede tardar hasta 60 segundos la primera vez.</span>
                </div>
            )}

            {/* KPIs */}
            {!loading && (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                    {KPI_CARDS.map((k, i) => (
                        <Card key={i} className="bg-white/5 border-white/10 text-white">
                            <CardContent className="p-4">
                                <p className="text-xs text-white/50 uppercase tracking-wide">{k.title}</p>
                                <p className={`text-xl font-bold mt-1 ${k.color || 'text-white'}`}>{k.value}</p>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Budget summary by branch */}
            {!loading && Object.keys(budgets).length > 0 && (
                <Card className="bg-white/5 border-white/10 text-white">
                    <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-white/70">Presupuesto por Sucursal</CardTitle>
                    </CardHeader>
                    <CardContent className="p-4 pt-0">
                        <div className="flex flex-wrap gap-3">
                            {Object.entries(budgets).map(([branch, b]) => {
                                const branchActual = selectedBranch === 'ALL' || selectedBranch === branch
                                    ? dailyReportData.days.reduce((acc, d) => {
                                        // sum seller actuals for this branch
                                        return acc + Object.values(d.sellerData).reduce((s, sd) => s + sd.actual, 0);
                                    }, 0)
                                    : 0;
                                const pct = b.total > 0 ? Math.round((periodActual / b.total) * 100) : 0;
                                return (
                                    <div
                                        key={branch}
                                        onClick={() => setSelectedBranch(branch)}
                                        className={`cursor-pointer rounded-lg p-3 border flex-1 min-w-[160px] transition-all ${
                                            selectedBranch === branch
                                                ? 'border-green-500 bg-green-950/30'
                                                : 'border-white/10 bg-white/5 hover:bg-white/10'
                                        }`}
                                    >
                                        <p className="text-xs text-white/50 truncate">{branch}</p>
                                        <p className="text-base font-bold text-white">{formatCurrency(b.total)}</p>
                                        {b.total > 0 && (
                                            <div className="mt-1 h-1 rounded-full bg-white/10">
                                                <div
                                                    className={`h-1 rounded-full ${periodFulfillment >= 100 ? 'bg-green-500' : 'bg-yellow-500'}`}
                                                    style={{ width: `${Math.min(pct, 100)}%` }}
                                                />
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Daily table */}
            {!loading && dailyReportData.days.length > 0 && (
                <Card className="bg-white/5 border-white/10 text-white overflow-hidden">
                    <CardHeader className="flex flex-row justify-between items-center">
                        <CardTitle className="text-base">
                            Detalle Diario — {selectedBranch === 'ALL' ? 'Todas las Sucursales' : selectedBranch}
                            <span className="ml-3 text-sm text-white/40 font-normal">
                                {stats.totalTickets} pedidos · {formatCurrency(periodActual)} ventas
                            </span>
                        </CardTitle>
                        <Button size="sm" variant="outline" onClick={handleExportPDF} className="border-white/10 text-white">
                            <Download className="mr-2 h-4 w-4" /> PDF
                        </Button>
                    </CardHeader>
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs md:text-sm">
                            <thead className="bg-white/5 text-white/60">
                                <tr>
                                    <th className="p-3 text-left sticky left-0 bg-slate-900/80">Fecha</th>
                                    <th className="p-3 text-right">Meta</th>
                                    <th className="p-3 text-right">Real</th>
                                    <th className="p-3 text-center">%</th>
                                    {dailyReportData.sellersWithGoals.map(s => (
                                        <th key={s.name} className="p-3 text-right border-l border-white/5">
                                            {s.name.split(' ')[0]}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-white/5">
                                {dailyReportData.days.map(day => (
                                    <tr key={day.date} className="hover:bg-white/5 transition-colors">
                                        <td className="p-3 sticky left-0 bg-slate-950">
                                            <div className="font-semibold">{day.date}</div>
                                            <div className="text-white/40 capitalize">{day.dayName}</div>
                                        </td>
                                        <td className="p-3 text-right text-white/50">{formatCurrency(day.estimatedSales)}</td>
                                        <td className="p-3 text-right font-bold text-green-400">{formatCurrency(day.totalDaySales)}</td>
                                        <td className={`p-3 text-center font-bold ${day.fulfillment >= 100 ? 'text-green-400' : day.fulfillment > 0 ? 'text-yellow-400' : 'text-white/20'}`}>
                                            {day.fulfillment > 0 ? `${day.fulfillment.toFixed(0)}%` : '—'}
                                        </td>
                                        {dailyReportData.sellersWithGoals.map(s => {
                                            const sd = day.sellerData[s.name];
                                            if (!sd || (sd.actual === 0 && sd.goal === 0)) {
                                                return <td key={s.name} className="p-3 text-right text-white/20 border-l border-white/5">—</td>;
                                            }
                                            const pct = sd.goal > 0 ? (sd.actual / sd.goal) * 100 : null;
                                            return (
                                                <td key={s.name} className="p-3 text-right border-l border-white/5">
                                                    <div className="font-semibold text-white">{formatCurrency(sd.actual)}</div>
                                                    {sd.goal > 0 && (
                                                        <div className={`text-[10px] ${pct >= 100 ? 'text-green-400' : 'text-white/40'}`}>
                                                            {pct.toFixed(0)}% de {formatCurrency(sd.goal)}
                                                        </div>
                                                    )}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                ))}

                                {/* Totals row */}
                                <tr className="bg-white/10 font-bold">
                                    <td className="p-3 text-white sticky left-0 bg-slate-800">TOTAL</td>
                                    <td className="p-3 text-right text-white/70">{formatCurrency(periodBudget)}</td>
                                    <td className="p-3 text-right text-green-400">{formatCurrency(periodActual)}</td>
                                    <td className={`p-3 text-center ${periodFulfillment >= 100 ? 'text-green-400' : 'text-yellow-400'}`}>
                                        {periodBudget > 0 ? `${periodFulfillment.toFixed(0)}%` : '—'}
                                    </td>
                                    {dailyReportData.sellersWithGoals.map(s => {
                                        const total = dailyReportData.days.reduce((acc, d) =>
                                            acc + (d.sellerData[s.name]?.actual || 0), 0);
                                        return (
                                            <td key={s.name} className="p-3 text-right border-l border-white/10 text-green-400">
                                                {formatCurrency(total)}
                                            </td>
                                        );
                                    })}
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </Card>
            )}

            {/* Empty state */}
            {!loading && !dailyReportData.days.length && report && (
                <div className="text-center py-12 text-white/40">
                    <BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
                    <p>No hay datos de ventas para el período seleccionado.</p>
                    <p className="text-sm mt-1">Intenta con un rango diferente o verifica la conexión con DynamiaERP.</p>
                </div>
            )}

            {/* Budget Dialog */}
            {isBudgetOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur p-4">
                    <BudgetDialog
                        branches={branches.filter(b => b !== 'ALL' && !b.includes('ALMACEN'))}
                        initialBudgets={budgets}
                        sellers={availableSellers}
                        onSave={handleBudgetSave}
                        onClose={() => setIsBudgetOpen(false)}
                    />
                </div>
            )}
        </div>
    );
}

// --- Budget Dialog ---
function BudgetDialog({ branches, initialBudgets, sellers, onSave, onClose }) {
    const [localBudgets, setLocalBudgets] = useState(() => {
        const b = { ...initialBudgets };
        branches.forEach(name => {
            if (!b[name]) b[name] = { ...defaultBranchBudget, sellerGoals: sellers.map(s => ({ ...s })) };
            else {
                const existing = new Set(b[name].sellerGoals.map(s => s.name));
                sellers.forEach(s => { if (!existing.has(s.name)) b[name].sellerGoals.push({ ...s }); });
            }
        });
        return b;
    });

    const [activeBranch, setActiveBranch] = useState(branches[0] || '');
    const cur = localBudgets[activeBranch] || defaultBranchBudget;

    const updateField = (field, val) =>
        setLocalBudgets(prev => ({ ...prev, [activeBranch]: { ...prev[activeBranch], [field]: val } }));

    const updateSeller = (idx, field, val) => {
        const goals = [...cur.sellerGoals];
        goals[idx] = { ...goals[idx], [field]: val };
        updateField('sellerGoals', goals);
    };

    const globalTotal = Object.values(localBudgets).reduce((a, b) => a + (b.total || 0), 0);

    return (
        <div className="bg-slate-950 border border-white/10 rounded-xl w-full max-w-4xl h-[85vh] flex flex-col shadow-2xl">
            <div className="p-5 border-b border-white/10 flex justify-between items-center">
                <div>
                    <h3 className="text-lg font-bold text-white">Configuración de Presupuesto</h3>
                    <p className="text-sm text-white/40">Total global: <span className="text-green-400 font-bold">{formatCurrency(globalTotal)}</span></p>
                </div>
                <Button variant="ghost" onClick={onClose} className="text-white/60 hover:text-white"><X className="h-5 w-5" /></Button>
            </div>

            <div className="flex-1 overflow-hidden flex">
                {/* Sidebar */}
                <div className="w-44 shrink-0 border-r border-white/10 p-2 space-y-1 overflow-y-auto">
                    {branches.map(b => (
                        <button
                            key={b}
                            onClick={() => setActiveBranch(b)}
                            className={`w-full text-left px-3 py-2 rounded text-sm transition-all ${
                                activeBranch === b ? 'bg-green-600 text-white' : 'text-white/60 hover:bg-white/5'
                            }`}
                        >
                            <div>{b}</div>
                            <div className="text-xs opacity-60">{formatCurrency(localBudgets[b]?.total || 0)}</div>
                        </button>
                    ))}
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-6 space-y-6">
                    {/* Budget amount */}
                    <div>
                        <label className="text-white/70 text-sm block mb-1">Presupuesto Mensual — {activeBranch}</label>
                        <div className="relative">
                            <DollarSign className="absolute left-3 top-2.5 h-4 w-4 text-white/50" />
                            <input
                                type="number"
                                className="w-full bg-white/5 border border-white/10 rounded px-3 py-2 pl-9 text-white text-xl font-bold"
                                value={cur.total || 0}
                                onChange={e => updateField('total', Number(e.target.value))}
                            />
                        </div>
                    </div>

                    {/* Daily weights */}
                    <div className="border border-white/10 rounded-lg p-4 bg-white/5">
                        <h4 className="text-white font-medium mb-3 text-sm">Pesos por Día de Semana</h4>
                        <div className="grid grid-cols-7 gap-2">
                            {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map((d, i) => (
                                <div key={d} className="text-center">
                                    <div className="text-xs text-white/50 mb-1">{d}</div>
                                    <input
                                        type="number" step="0.1" min="0"
                                        className="w-full bg-black/30 border border-white/10 rounded text-center text-xs text-white py-1"
                                        value={(cur.dailyWeights?.[i]?.p ?? defaultDailyWeights[i].p).toFixed(2)}
                                        onChange={e => {
                                            const val = Number(e.target.value);
                                            const prev = cur.dailyWeights || defaultDailyWeights;
                                            updateField('dailyWeights', {
                                                ...prev,
                                                [i]: { p: val, s: parseFloat((val * 1.5).toFixed(2)) }
                                            });
                                        }}
                                    />
                                </div>
                            ))}
                        </div>
                        <p className="text-xs text-white/30 mt-2">Viernes y Sábado = mayor tráfico. Los pesos secundarios se calculan automáticamente (×1.5).</p>
                    </div>

                    {/* Seller goals */}
                    <div>
                        <h4 className="text-white font-medium mb-3 text-sm">Metas por Vendedor ({cur.sellerGoals?.length || 0})</h4>
                        {cur.sellerGoals?.length === 0 && (
                            <p className="text-white/30 text-sm">Los vendedores aparecen aquí después de cargar el reporte de ventas.</p>
                        )}
                        <div className="space-y-2">
                            {cur.sellerGoals?.map((s, idx) => (
                                <div key={idx} className="flex items-center gap-2 bg-white/5 p-2 rounded border border-white/5">
                                    <span className="flex-1 text-sm text-white truncate">{s.name}</span>
                                    <select
                                        className="bg-black/30 border border-white/10 rounded text-xs text-white px-2 py-1"
                                        value={s.role}
                                        onChange={e => updateSeller(idx, 'role', e.target.value)}
                                    >
                                        <option value="principal">Experto</option>
                                        <option value="secundario">Standard</option>
                                    </select>
                                    <div className="relative w-24">
                                        <input
                                            type="number" min="0" max="100"
                                            className="w-full bg-black/30 border border-white/10 rounded text-right text-white pr-6 px-2 py-1 text-sm"
                                            value={s.goalPercentage}
                                            onChange={e => updateSeller(idx, 'goalPercentage', Number(e.target.value))}
                                        />
                                        <span className="absolute right-2 top-1.5 text-xs text-white/50">%</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {cur.sellerGoals?.reduce((a, s) => a + s.goalPercentage, 0) !== 100 && cur.sellerGoals?.length > 0 && (
                            <p className="text-yellow-400 text-xs mt-2">
                                ⚠ Los porcentajes suman {cur.sellerGoals.reduce((a, s) => a + s.goalPercentage, 0)}% (deberían sumar 100%)
                            </p>
                        )}
                    </div>
                </div>
            </div>

            <div className="p-4 border-t border-white/10 flex justify-between items-center">
                <p className="text-xs text-white/30">Los cambios aplican a todos los cálculos de esta sucursal.</p>
                <div className="flex gap-3">
                    <Button variant="ghost" onClick={onClose} className="text-white">Cancelar</Button>
                    <Button onClick={() => onSave(localBudgets)} className="bg-green-600 hover:bg-green-500 text-white font-bold">
                        Guardar Cambios
                    </Button>
                </div>
            </div>
        </div>
    );
}
